import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spin, Form, Input, InputNumber, Button, Rate, Select } from "antd";
import * as recruitmentApi from "../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../services/toast.config";
import type { CriteriaSchemaItem } from "../../../../core/data/redux/recruitment/types";

const { TextArea } = Input;
const { Option } = Select;

const PublicFeedbackForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schemaData, setSchemaData] = useState<{
    interview: any;
    schema: CriteriaSchemaItem[];
  } | null>(null);

  useEffect(() => {
    if (token) {
      loadSchema(token);
    }
  }, [token]);

  const loadSchema = async (t: string) => {
    try {
      setLoading(true);
      const res = await recruitmentApi.getFeedbackSchema(t);
      if (res) {
        setSchemaData(res);
      }
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.message || "Invalid or expired feedback link");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    if (!token) return;
    try {
      setSubmitting(true);
      
      const payload = {
        overall_rating: values.overall_rating,
        recommendation: values.recommendation,
        detailed_feedback: values.detailed_feedback,
        strengths: values.strengths,
        weaknesses: values.weaknesses,
        notes: values.notes,
        criteria_scores: values.criteria,
      };

      await recruitmentApi.submitPublicFeedback(token, payload);
      notify.success("Feedback submitted successfully. Thank you!");
      // Replace form with success message
      setSchemaData(null);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.message || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Spin size="large" />
      </div>
    );
  }

  // If no schemadata and not loading, show success or error state
  if (!schemaData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div className="text-center p-5 bg-white rounded shadow-sm border" style={{ maxWidth: 500 }}>
          <i className="ti ti-circle-check text-success mb-3 d-block" style={{ fontSize: 60 }} />
          <h3 className="fw-bold mb-2">Feedback Submitted</h3>
          <p className="text-muted mb-4">Your feedback has been successfully recorded. You may close this window safely.</p>
        </div>
      </div>
    );
  }

  const { interview, schema } = schemaData;
  const hasCriteria = schema && schema.length > 0;

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "50px" }}>
      <div className="text-white py-5 position-relative overflow-hidden mb-5" style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" }}>
        <div className="container">
          <h1 className="fw-bolder display-6 mb-2 text-white">Interview Feedback</h1>
          <p className="fs-5 text-white-50 mb-0">Evaluate <strong>{interview.candidate_name}</strong> for {interview.job_title} ({interview.round_name})</p>
        </div>
      </div>

      <div className="container" style={{ maxWidth: 800 }}>
        <div className="card shadow-sm border-0 rounded-4">
          <div className="card-body p-5">
            <Form form={form} layout="vertical" onFinish={onFinish}>
              {hasCriteria && (
                <>
                  <h5 className="fw-bold mb-4 border-bottom pb-2">Rubric Evaluation</h5>
                  <div className="mb-5">
                    {schema.map((crit) => (
                      <div key={crit.key} className="mb-4 p-3 bg-light rounded border">
                        <div className="d-flex justify-content-between mb-2 align-items-center">
                          <label className="fw-semibold mb-0">{crit.label}</label>
                          <span className="badge bg-secondary">Weight: {crit.weight}%</span>
                        </div>
                        {crit.description && <p className="text-muted small mb-3">{crit.description}</p>}
                        <Form.Item
                          name={['criteria', crit.key]}
                          rules={[{ required: true, message: "Score is required" }]}
                          noStyle
                        >
                          <InputNumber
                            min={1} max={10}
                            placeholder="Score out of 10"
                            className="w-100"
                            style={{ maxWidth: 150 }}
                            addonAfter="/ 10"
                          />
                        </Form.Item>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h5 className="fw-bold mb-4 border-bottom pb-2">Summary & Impression</h5>

              <Form.Item
                name="overall_rating"
                label="Overall Rating (1-5)"
                rules={[{ required: true, message: 'Please provide an overall rating' }]}
              >
                <Rate />
              </Form.Item>

              <Form.Item
                name="detailed_feedback"
                label="Detailed Feedback"
                rules={[{ required: true, message: 'Please provide detailed feedback' }]}
              >
                <TextArea rows={5} placeholder="Share your detailed thoughts about the candidate's performance..." />
              </Form.Item>

              <div className="row">
                <div className="col-md-6">
                  <Form.Item name="strengths" label="Key Strengths">
                    <TextArea rows={3} placeholder="What did the candidate do well?" />
                  </Form.Item>
                </div>
                <div className="col-md-6">
                  <Form.Item name="weaknesses" label="Areas for Improvement">
                    <TextArea rows={3} placeholder="Where did the candidate struggle?" />
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                name="recommendation"
                label="Hiring Recommendation"
                rules={[{ required: true, message: 'Please select a recommendation' }]}
              >
                <Select placeholder="Select your recommendation">
                  <Option value="strong_hire"><span className="text-success fw-bold">Strong Hire</span></Option>
                  <Option value="hire">Hire</Option>
                  <Option value="neutral">Neutral</Option>
                  <Option value="no_hire">No Hire</Option>
                  <Option value="strong_no_hire"><span className="text-danger fw-bold">Strong No Hire</span></Option>
                </Select>
              </Form.Item>

              <Form.Item name="notes" label="Private Notes (Optional)">
                <TextArea rows={2} placeholder="Any additional internal notes..." />
              </Form.Item>

              <div className="d-flex justify-content-end mt-5 pt-3 border-top">
                <Button type="primary" htmlType="submit" size="large" loading={submitting} className="px-5 rounded-pill">
                  Submit Feedback
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFeedbackForm;
